import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { BINDING_DEFAULT_Y, INIT_Y } from '../../variable/constant'
import createStem from '../../line/createStem'
import getPointsArr from '../../line/getPointsArr'
import { reomveObjectFromScene, removeTreeFromScene } from '../../utils/index'

class GlbFlowerObject {
    constructor(initData) {
        const { resource, env, params, id, position } = initData;
        this.id = id || 'flower_' + new Date().getTime();
        this.resource = resource;
        this.env = env;
        this.renderType = 'glb';
        this.isGlbResource = true;
        this.modelMesh = {
            flowerMesh: null,
            stemMesh: null,
            touchHelperMesh: null,
            pickHelperMesh: null,
            aimGroup: null,
            twistGroup: null,
            contentGroup: null,
            headRotateTarget: null,
            stemAnchorObject: null,
            headCenterObject: null,
            stemEntryObject: null,
        };
        this.realMesh = {
            flowerMesh: null,
            stemMesh: null,
        };
        this.params = params || {
            bindingY: 0,
            faceIndex: 0,
            headAngle: 0,
            size: 1,
            visible: true,
            showStem: true,
            locked: false,
        };
        if (this.params.visible === undefined) {
            this.params.visible = true;
        }
        if (this.params.showStem === undefined) {
            this.params.showStem = true;
        }
        if (this.params.locked === undefined) {
            this.params.locked = false;
        }
        if (this.params.headAngle === undefined) {
            this.params.headAngle = 0;
        }
        if (this.params.stemRadius === undefined) {
            this.params.stemRadius = parseFloat(this.resource.detail.stem && this.resource.detail.stem.radiusStem) || 2;
        }
        this.modelConfig = this.resource.detail.modelConfig || {};
        this.baseScale = parseFloat(this.resource.detail.defaultScale) || 1;
        this.modelBounds = null;
        this.modelMeta = {
            height: 1,
            radius: 0.5,
        };
        this.headRotateInitialRotation = null;
        this.helperVisible = this.params.visible;
        this.loader = new GLTFLoader();
        this.loader.setMeshoptDecoder(MeshoptDecoder);
        this.initPromise = this.init(position);
    }

    getInitialPosition(position) {
        if (position) {
            return new THREE.Vector3(position.x, position.y, position.z);
        }
        let shifting = sessionStorage.getItem('shifting') || 0;
        let viewVec3 = new THREE.Vector3().copy(this.env.three.viewController.object.position);
        viewVec3 = viewVec3.divideScalar(viewVec3.length());
        const startPosition = this.env.objects.jsContainer.position;
        let initVec3 = new THREE.Vector3(startPosition.x, INIT_Y, startPosition.z).addScaledVector(viewVec3, shifting - 0);
        sessionStorage.setItem('shifting', shifting - 0 + 5);
        return initVec3;
    }

    getStartPosition() {
        return this.env.objects.jsContainer.position;
    }

    hasPointValue(point) {
        return !!point && (
            point.x !== undefined ||
            point.y !== undefined ||
            point.z !== undefined
        );
    }

    getStemAnchor() {
        const stemAnchor = this.modelConfig.stemAnchor || {};
        return new THREE.Vector3(
            parseFloat(stemAnchor.x) || 0,
            parseFloat(stemAnchor.y) || 0,
            parseFloat(stemAnchor.z) || 0
        );
    }

    getNormalizeAnchor() {
        return this.getStemAnchor();
    }

    getResolvedConfigPoint(point) {
        const pointVector = point ? point.clone() : new THREE.Vector3();
        if (!this.modelBounds) {
            return pointVector;
        }
        return new THREE.Vector3(
            this.modelBounds.center.x + pointVector.x,
            this.modelBounds.min.y + pointVector.y,
            this.modelBounds.center.z + pointVector.z
        );
    }

    getLocalPointFromNormalize(point) {
        const resolvedPoint = this.getResolvedConfigPoint(point);
        const resolvedNormalizeAnchor = this.getResolvedConfigPoint(this.getNormalizeAnchor());
        return resolvedPoint.sub(resolvedNormalizeAnchor);
    }

    getHeadCenter() {
        const headCenter = this.modelConfig.headCenter || this.modelConfig.bendGuide || this.modelConfig.headGuide || {};
        if (this.hasPointValue(headCenter)) {
            return new THREE.Vector3(
                parseFloat(headCenter.x) || 0,
                parseFloat(headCenter.y) || 0,
                parseFloat(headCenter.z) || 0
            );
        }
        if (!this.modelBounds) {
            return this.getStemAnchor();
        }
        return new THREE.Vector3(
            0,
            this.modelBounds.center.y - this.modelBounds.min.y,
            0
        );
    }

    getStemEntry() {
        const stemEntry = this.modelConfig.stemEntry || this.modelConfig.stemGuide || this.modelConfig.entryGuide || {};
        if (this.hasPointValue(stemEntry)) {
            return new THREE.Vector3(
                parseFloat(stemEntry.x) || 0,
                parseFloat(stemEntry.y) || 0,
                parseFloat(stemEntry.z) || 0
            );
        }
        const stemAnchor = this.getStemAnchor();
        const headCenter = this.getHeadCenter();
        const stemDirection = stemAnchor.clone().sub(headCenter);
        if (stemDirection.lengthSq() > 0.0001) {
            return stemAnchor.clone().add(stemDirection.multiplyScalar(0.6));
        }
        return stemAnchor.clone().add(new THREE.Vector3(0, -0.25, 0));
    }

    getInitialRotationEuler() {
        const initialRotation = this.modelConfig.initialRotation || {};
        return new THREE.Euler(
            THREE.MathUtils.degToRad(parseFloat(initialRotation.x) || 0),
            THREE.MathUtils.degToRad(parseFloat(initialRotation.y) || 0),
            THREE.MathUtils.degToRad(parseFloat(initialRotation.z) || 0),
            'XYZ'
        );
    }

    getStemAxisVector() {
        const shouldUseMarkerAxis = this.modelConfig.stemAxisFromMarkers !== false;
        const hasStemEntryConfig = this.hasPointValue(
            this.modelConfig.stemEntry || this.modelConfig.stemGuide || this.modelConfig.entryGuide
        );
        if (shouldUseMarkerAxis && hasStemEntryConfig) {
            const markerAxis = this.getStemAnchor().clone().sub(this.getStemEntry());
            if (markerAxis.lengthSq() > 0.0001) {
                return markerAxis.normalize();
            }
        }
        const hasHeadCenterConfig = this.hasPointValue(
            this.modelConfig.headCenter || this.modelConfig.bendGuide || this.modelConfig.headGuide
        );
        if (shouldUseMarkerAxis && hasHeadCenterConfig) {
            const markerAxis = this.getStemAnchor().clone().sub(this.getHeadCenter());
            if (markerAxis.lengthSq() > 0.0001) {
                return markerAxis.normalize();
            }
        }
        const stemAxis = this.modelConfig.stemAxis;
        if (typeof stemAxis === 'string') {
            switch (stemAxis.toLowerCase()) {
                case 'x':
                    return new THREE.Vector3(1, 0, 0);
                case 'z':
                    return new THREE.Vector3(0, 0, 1);
                default:
                    return new THREE.Vector3(0, 1, 0);
            }
        }
        if (stemAxis && (stemAxis.x !== undefined || stemAxis.y !== undefined || stemAxis.z !== undefined)) {
            const axisVector = new THREE.Vector3(
                parseFloat(stemAxis.x) || 0,
                parseFloat(stemAxis.y) || 0,
                parseFloat(stemAxis.z) || 0
            );
            if (axisVector.lengthSq() > 0.0001) {
                return axisVector.normalize();
            }
        }
        return new THREE.Vector3(0, 1, 0);
    }

    getAlignedStemAxisVector() {
        return this.getStemAxisVector().clone().applyEuler(this.getInitialRotationEuler()).normalize();
    }

    getHeadAxisKey() {
        const axis = (this.modelConfig.headAxis || this.modelConfig.rotateAxis || 'y').toLowerCase();
        return ['x', 'y', 'z'].includes(axis) ? axis : 'y';
    }

    supportsHeadRotate() {
        const supportedTools = this.modelConfig.supportedTools || [];
        return (
            supportedTools.includes('headRotate') ||
            supportedTools.includes('directionRotate') ||
            !!this.modelConfig.headControlType ||
            !!this.modelConfig.headNodeName
        );
    }

    getHeadRotateStep() {
        const step = parseFloat(this.modelConfig.headAngleStep);
        return step > 0 ? step : 15;
    }

    getHeadAngleRange() {
        const min = parseFloat(this.modelConfig.headAngleMin);
        const max = parseFloat(this.modelConfig.headAngleMax);
        if (!Number.isNaN(min) && !Number.isNaN(max)) {
            return { min, max };
        }
        return null;
    }

    normalizeHeadAngle(angle) {
        const range = this.getHeadAngleRange();
        if (range) {
            return THREE.MathUtils.clamp(angle, range.min, range.max);
        }
        let normalized = angle % 360;
        if (normalized > 180) {
            normalized -= 360;
        }
        if (normalized <= -180) {
            normalized += 360;
        }
        return normalized;
    }

    applyContentOrientation() {
        const contentGroup = this.modelMesh.contentGroup;
        if (!contentGroup) {
            return;
        }
        const initialRotation = this.getInitialRotationEuler();
        contentGroup.rotation.copy(initialRotation);
        if (!this.supportsHeadRotate()) {
            return;
        }
        const headRotateTarget = this.modelMesh.headRotateTarget;
        if (!headRotateTarget) {
            return;
        }
        if (this.headRotateInitialRotation) {
            headRotateTarget.rotation.copy(this.headRotateInitialRotation);
        } else {
            headRotateTarget.rotation.set(0, 0, 0);
        }
        const headAngle = THREE.MathUtils.degToRad(parseFloat(this.params.headAngle) || 0);
        const axisKey = this.getHeadAxisKey();
        headRotateTarget.rotation[axisKey] += headAngle;
    }

    getStemAnchorPosition() {
        const stemAnchorObject = this.modelMesh.stemAnchorObject;
        if (!stemAnchorObject) {
            return this.modelMesh.flowerMesh ? this.modelMesh.flowerMesh.position.clone() : new THREE.Vector3();
        }
        return stemAnchorObject.getWorldPosition(new THREE.Vector3());
    }

    getHeadCenterPosition() {
        const headCenterObject = this.modelMesh.headCenterObject;
        if (!headCenterObject) {
            return this.modelMesh.flowerMesh ? this.modelMesh.flowerMesh.position.clone() : new THREE.Vector3();
        }
        return headCenterObject.getWorldPosition(new THREE.Vector3());
    }

    getStemEntryPosition() {
        const stemEntryObject = this.modelMesh.stemEntryObject;
        if (!stemEntryObject) {
            return this.getStemAnchorPosition();
        }
        return stemEntryObject.getWorldPosition(new THREE.Vector3());
    }

    getControlObject() {
        // 命中仍然优先使用隐藏辅助球，但实际拖拽回到花材本体，
        // 避免拖拽代理和花头/朝向联动互相抢位置造成抖动。
        return this.modelMesh.flowerMesh;
    }

    getMovePosition() {
        return this.modelMesh.flowerMesh ? this.modelMesh.flowerMesh.position.clone() : new THREE.Vector3();
    }

    syncControlObjectPosition(targetObject) {
        if (!this.modelMesh.flowerMesh || targetObject !== this.modelMesh.pickHelperMesh) {
            return;
        }
        const currentHeadCenterPosition = this.getHeadCenterPosition();
        const delta = new THREE.Vector3().subVectors(targetObject.position, currentHeadCenterPosition);
        if (delta.lengthSq() < 0.000001) {
            return;
        }
        this.modelMesh.flowerMesh.position.add(delta);
    }

    getStemCurveOptions(stemAnchorPosition = this.getStemAnchorPosition()) {
        const configuredStrength = this.modelConfig.bendStrength;
        const bendStrength = configuredStrength === undefined ? 1 : parseFloat(configuredStrength);
        if (bendStrength <= 0) {
            return {
                forceStraight: true,
            };
        }
        const headCenterPosition = this.getHeadCenterPosition();
        const stemEntryPosition = this.getStemEntryPosition();
        const tipTangent = stemEntryPosition.clone().sub(stemAnchorPosition);
        if (tipTangent.lengthSq() < 0.0001) {
            return {
                forceStraight: true,
            };
        }
        return {
            sourcePosition: headCenterPosition,
            tipTangent,
            tipTangentStrength: Math.max(stemAnchorPosition.distanceTo(stemEntryPosition) * 0.75, 28) * bendStrength,
        };
    }

    createTouchHelper() {
        const helperMesh = new THREE.Mesh(
            new THREE.SphereGeometry(10, 16, 16),
            new THREE.MeshBasicMaterial({
                color: this.resource.detail.flower.color,
                transparent: true,
                opacity: 0,
                depthWrite: false,
            })
        );
        helperMesh.parentObject = this;
        helperMesh.visible = this.params.visible;
        this.modelMesh.touchHelperMesh = helperMesh;
        this.env.three.scene.add(helperMesh);

        const pickHelperMesh = new THREE.Mesh(
            new THREE.SphereGeometry(10, 16, 16),
            new THREE.MeshBasicMaterial({
                transparent: true,
                opacity: 0,
                depthWrite: false,
            })
        );
        pickHelperMesh.parentObject = this;
        this.modelMesh.pickHelperMesh = pickHelperMesh;
        this.env.three.scene.add(pickHelperMesh);
    }

    syncTouchHelperState() {
        if (!this.modelMesh.touchHelperMesh) {
            return;
        }
        const helperPosition = this.getHeadCenterPosition();
        this.modelMesh.touchHelperMesh.position.copy(helperPosition);
        // 焦点球和贴图花材保持一致的视觉尺寸，避免 GLB 因模型尺度不同而被放得过大。
        this.modelMesh.touchHelperMesh.scale.setScalar(1);
        this.modelMesh.touchHelperMesh.visible = this.helperVisible;
        this.modelMesh.touchHelperMesh.material.opacity = this.env.params.render.touchHelper ? 0.35 : 0;

        if (this.modelMesh.pickHelperMesh) {
            const pickPosition = this.getHeadCenterPosition();
            const pickRadius = Math.max(this.modelMeta.radius * this.params.size * this.baseScale * 0.6, 18);
            const isDraggingWithPickHelper =
                this.env.params.draggingFlowerId === this.id &&
                this.env.three.objController.object === this.modelMesh.pickHelperMesh;
            if (!isDraggingWithPickHelper) {
                this.modelMesh.pickHelperMesh.position.copy(pickPosition);
            }
            this.modelMesh.pickHelperMesh.scale.setScalar(pickRadius / 10);
            this.modelMesh.pickHelperMesh.visible = this.params.visible;
        }
    }

    updateStemGeometry() {
        if (!this.modelMesh.stemMesh) {
            return;
        }
        const startPosition = this.getStartPosition();
        const stemAnchorPosition = this.getStemAnchorPosition();
        this.modelMesh.stemMesh.geometry.setPositions(
            getPointsArr({
                startPosition,
                position: stemAnchorPosition,
                ...this.getStemCurveOptions(stemAnchorPosition),
                bindingType: this.env.params.render.bindingType,
                bindingY: BINDING_DEFAULT_Y + this.params.bindingY
            })
        );
        this.modelMesh.stemMesh.material.linewidth = this.params.stemRadius;
    }

    async init(position) {
        const flowerMesh = new THREE.Group();
        const initPosition = this.getInitialPosition(position);
        flowerMesh.position.copy(initPosition);
        flowerMesh.parentObject = this;
        this.modelMesh.flowerMesh = flowerMesh;
        this.env.three.scene.add(flowerMesh);

        const gltf = await this.loader.loadAsync(this.resource.detail.url);
        const content = gltf.scene || gltf.scenes?.[0] || new THREE.Group();
        const box = new THREE.Box3().setFromObject(content);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        this.modelBounds = {
            center: center.clone(),
            min: box.min.clone(),
            max: box.max.clone(),
        };
        this.modelMeta.height = Math.max(size.y, 1);
        this.modelMeta.radius = Math.max(size.x, size.z, 0.5) / 2;

        const resolvedNormalizeAnchor = this.getResolvedConfigPoint(this.getNormalizeAnchor());
        content.position.sub(resolvedNormalizeAnchor);
        content.traverse((node) => {
            if (!node.isMesh) {
                return;
            }
            node.castShadow = true;
            node.receiveShadow = true;
        });

        const aimGroup = new THREE.Group();
        const twistGroup = new THREE.Group();
        const contentGroup = new THREE.Group();
        let headRotateTarget = null;
        if (this.modelConfig.headControlType === 'node' && this.modelConfig.headNodeName) {
            const headNode = content.getObjectByName(this.modelConfig.headNodeName);
            if (headNode) {
                headRotateTarget = headNode;
                this.headRotateInitialRotation = headNode.rotation.clone();
            }
        }

        contentGroup.add(content);
        twistGroup.add(contentGroup);
        aimGroup.add(twistGroup);
        flowerMesh.add(aimGroup);

        if (!headRotateTarget && this.supportsHeadRotate()) {
            headRotateTarget = twistGroup;
        }

        const stemAnchorObject = new THREE.Object3D();
        stemAnchorObject.position.copy(this.getLocalPointFromNormalize(this.getStemAnchor()));
        flowerMesh.add(stemAnchorObject);

        const headCenterObject = new THREE.Object3D();
        headCenterObject.position.copy(this.getLocalPointFromNormalize(this.getHeadCenter()));
        contentGroup.add(headCenterObject);

        const stemEntryObject = new THREE.Object3D();
        stemEntryObject.position.copy(this.getLocalPointFromNormalize(this.getStemEntry()));
        contentGroup.add(stemEntryObject);

        this.modelMesh.aimGroup = aimGroup;
        this.modelMesh.twistGroup = twistGroup;
        this.modelMesh.contentGroup = contentGroup;
        this.modelMesh.headRotateTarget = headRotateTarget || contentGroup;
        if (!this.headRotateInitialRotation && this.modelMesh.headRotateTarget !== twistGroup) {
            this.headRotateInitialRotation = this.modelMesh.headRotateTarget.rotation.clone();
        }
        this.modelMesh.stemAnchorObject = stemAnchorObject;
        this.modelMesh.headCenterObject = headCenterObject;
        this.modelMesh.stemEntryObject = stemEntryObject;

        this.modelMesh.stemMesh = createStem({
            startPosition: this.getStartPosition(),
            position: this.getStemAnchorPosition(),
            ...this.getStemCurveOptions(this.getStemAnchorPosition()),
            bindingType: this.env.params.render.bindingType,
            bindingY: BINDING_DEFAULT_Y + this.params.bindingY,
            dom: this.env.params.dom,
            materialOption: {
                color: this.resource.detail.stem.color,
                linewidth: this.params.stemRadius
            }
        });
        this.env.three.scene.add(this.modelMesh.stemMesh);
        this.createTouchHelper();
        this.updateModel();
    }

    updateModel(sourceStartPosition) {
        const flowerMesh = this.modelMesh.flowerMesh;
        if (!flowerMesh) {
            return;
        }
        const startPosition = this.getStartPosition();
        const bindingY = BINDING_DEFAULT_Y + this.params.bindingY;

        if (sourceStartPosition) {
            let sourcePosition = flowerMesh.position;
            sourcePosition.x = (sourcePosition.x - sourceStartPosition.x) + startPosition.x;
            sourcePosition.y = (sourcePosition.y - sourceStartPosition.y) + startPosition.y;
            sourcePosition.z = (sourcePosition.z - sourceStartPosition.z) + startPosition.z;
        }

        const scale = this.params.size * this.baseScale;
        flowerMesh.scale.set(scale, scale, scale);
        const direction = new THREE.Vector3(
            flowerMesh.position.x - startPosition.x,
            flowerMesh.position.y - bindingY,
            flowerMesh.position.z - startPosition.z
        );
        if (this.modelMesh.aimGroup) {
            if (direction.lengthSq() > 0.0001) {
                this.modelMesh.aimGroup.quaternion.setFromUnitVectors(
                    this.getAlignedStemAxisVector(),
                    direction.normalize()
                );
            } else {
                this.modelMesh.aimGroup.quaternion.identity();
            }
        }
        this.applyContentOrientation();
        this.updateStemGeometry();
        this.syncTouchHelperState();
    }

    clearReal(modelVisiable = true) {
        const visible = this.params.visible && modelVisiable;
        this.helperVisible = visible;
        if (this.modelMesh.flowerMesh) {
            this.modelMesh.flowerMesh.visible = visible;
        }
        if (this.modelMesh.stemMesh) {
            this.modelMesh.stemMesh.visible = visible && this.params.showStem;
        }
        this.syncTouchHelperState();
    }

    setVisible(visible) {
        this.params.visible = visible;
        if (!visible) {
            this.clearReal(false);
            return;
        }
        this.helperVisible = true;
        this.clearReal(true);
    }

    setStemVisible(showStem) {
        this.params.showStem = showStem;
        if (this.modelMesh.stemMesh) {
            this.modelMesh.stemMesh.visible = this.params.visible && showStem;
        }
    }

    setStemRadius(stemRadius) {
        this.params.stemRadius = Math.max(1, Math.round(stemRadius - 0 || 1));
        if (this.modelMesh.stemMesh) {
            this.modelMesh.stemMesh.material.linewidth = this.params.stemRadius;
            this.modelMesh.stemMesh.material.needsUpdate = true;
        }
    }

    setLocked(locked) {
        this.params.locked = locked;
    }

    getLayerData() {
        return {
            id: this.id,
            name: this.resource.name,
            resourceId: this.resource.id,
            visible: this.params.visible,
            showStem: this.params.showStem,
            stemRadius: this.params.stemRadius,
            locked: this.params.locked
        };
    }

    remove() {
        this.env.three.objController.detach();
        if (this.modelMesh.touchHelperMesh) {
            reomveObjectFromScene(this.env.three.scene, this.modelMesh.touchHelperMesh);
            this.modelMesh.touchHelperMesh = null;
        }
        if (this.modelMesh.pickHelperMesh) {
            reomveObjectFromScene(this.env.three.scene, this.modelMesh.pickHelperMesh);
            this.modelMesh.pickHelperMesh = null;
        }
        if (this.modelMesh.stemMesh) {
            reomveObjectFromScene(this.env.three.scene, this.modelMesh.stemMesh);
            this.modelMesh.stemMesh = null;
        }
        if (this.modelMesh.flowerMesh) {
            removeTreeFromScene(this.env.three.scene, this.modelMesh.flowerMesh);
            this.modelMesh.flowerMesh = null;
        }
        for (let i = 0; i < this.env.objects.flowers.length; i++) {
            if (this.id === this.env.objects.flowers[i].id) {
                this.env.objects.flowers.splice(i, 1);
                break;
            }
        }
        if (this.env.selectedFlowerId === this.id) {
            this.env.clearFlowerSelection();
        }
        this.env.syncLayerState();
    }

    changeResource(resource) {
        this.resource = resource;
        this.baseScale = parseFloat(resource.detail.defaultScale) || this.baseScale;
        this.modelConfig = resource.detail.modelConfig || {};
        this.applyContentOrientation();
        this.updateStemGeometry();
    }

    rotate(val) {
        if (!this.supportsHeadRotate()) {
            return this.params.faceIndex;
        }
        const nextAngle = this.normalizeHeadAngle(
            (parseFloat(this.params.headAngle) || 0) + val * this.getHeadRotateStep()
        );
        this.params.headAngle = nextAngle;
        this.params.faceIndex = Math.round(nextAngle);
        this.applyContentOrientation();
        return this.params.faceIndex;
    }
}

export default GlbFlowerObject;
